import reverse from "lodash/reverse";

import { SerializedComponent } from "../../components/SerializedApp";
import { BuilderAction } from "../../Database/builder-actions";
import { defined, mapFilterUndefined } from "../../Support";
import {
    AllowedDataEdits,
    AppDescriptionContext,
    ComponentBuilder,
    ComponentConfiguratorContext,
    CompoundActionPropertyDescriptorCase,
    EditedColumns,
    InteractiveComponentConfiguratorContext,
    makeEmptyMutableAllowedDataEdits,
    mergeAllowedDataEdits,
    RewritingComponentConfiguratorContext,
    WireActionHydrator,
    WireActionRunner,
    WireInflationBackend,
    WirePredicate,
} from "../components";
import { PropertySection } from "../components/property-sections";
import { getCompoundActionProperty, makeCompoundActionProperty } from "../components/property-utils";
import {
    ActionDescription,
    ActionKind,
    areTableNamesEqual,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    PropertyKind,
    getTableName,
    Formula,
} from "../description";
import { compileFormula } from "../formulas";
import { ActionDescriptor } from "./action-descriptor";
import { BaseActionHandler } from "./base";
import { FlowActionNode, processCompoundActionFlow } from "./compound";
import { handlerForActionKind } from ".";
import { handlerForPropertyKind } from "../components/description-handlers";
import { inflateActions } from "../../wire/utils";

export interface CompoundActionDescription extends ActionDescription {
    readonly kind: ActionKind.Compound;
    readonly actionID: PropertyDescription;
}

function getBuilderAction(
    desc: CompoundActionDescription | undefined,
    adc: AppDescriptionContext
): BuilderAction | undefined {
    const actionID = getCompoundActionProperty(desc?.actionID);
    if (actionID === undefined) return undefined;
    return adc.getBuilderAction(actionID);
}

const propertyDescriptorCase: CompoundActionPropertyDescriptorCase = {
    kind: PropertyKind.CompoundAction,
};

export class CompoundActionHandler extends BaseActionHandler<CompoundActionDescription> {
    public readonly kind = ActionKind.Compound;
    // FIXME: Should this be configurable?
    public readonly iconName = true;

    public getDescriptor(
        desc: CompoundActionDescription | undefined,
        _tables: InputOutputTables | undefined,
        ccc: ComponentConfiguratorContext,
        _mutatingScreenKind: MutatingScreenKind | undefined
    ): ActionDescriptor {
        const action = getBuilderAction(desc, ccc);

        return {
            name: action?.title ?? "Compound",
            needsScreenContext: true,
            properties: [
                {
                    ...propertyDescriptorCase,
                    label: "Action",
                    property: { name: "actionID" },
                    section: PropertySection.Data,
                },
            ],
        };
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: CompoundActionDescription,
        tables: InputOutputTables,
        screenContext: SerializedComponent | undefined,
        outputContext: SerializedComponent,
        mutatingScreenKind: MutatingScreenKind | undefined,
        detailScreenTitle: SerializedComponent | undefined,
        itemID: SerializedComponent | undefined,
        _onSuccess: SerializedComponent | undefined,
        fromInlineList: boolean
    ): SerializedComponent | undefined {
        const { b } = builder;

        const compound = getBuilderAction(desc, builder)?.action;
        if (compound === undefined) return undefined;

        function compileFlow(flow: FlowActionNode): SerializedComponent {
            const handlers = mapFilterUndefined(flow.actions, ({ actionDescription }) => {
                const handler = handlerForActionKind(actionDescription.kind);
                if (handler === undefined) return undefined;
                return { actionDescription, handler };
            });
            const pendingSequence = handlers.reduceRight<SerializedComponent[]>(
                (seq, { actionDescription, handler }) => {
                    const action = handler.makeAction(
                        builder,
                        actionDescription,
                        tables,
                        screenContext,
                        outputContext,
                        mutatingScreenKind,
                        detailScreenTitle,
                        itemID,
                        handler.handlesSequencing ? b.sequence(seq) : undefined,
                        fromInlineList
                    );
                    if (action === undefined) return seq;
                    if (handler.handlesSequencing) {
                        return [action];
                    } else {
                        return [action, ...seq];
                    }
                },
                []
            );
            return b.sequence(pendingSequence);
        }

        let action: SerializedComponent;
        if (compound.elseFlow !== undefined) {
            action = compileFlow(compound.elseFlow);
        } else {
            action = b.noAction();
        }

        for (const flow of reverse(Array.from(compound.conditionalFlows))) {
            const condition = compileFormula(
                builder,
                tables.input,
                flow.condition,
                false,
                false,
                "action conditions shouldn't need unique IDs"
            );
            if (!condition.success) continue;

            let conditionValue = condition.value;
            if (screenContext !== undefined) {
                conditionValue = b.bindContextToValue(conditionValue, screenContext);
            }

            action = b.if(conditionValue, compileFlow(flow.flow), action);
        }

        return action;
    }

    public getEditedColumns(
        desc: CompoundActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): EditedColumns | undefined {
        const handler = defined(handlerForPropertyKind(PropertyKind.CompoundAction));
        return handler.getEditedColumns(
            propertyDescriptorCase,
            desc.actionID,
            desc,
            tables,
            mutatingScreenKind,
            ccc,
            true
        );
    }

    public getAllowedDataEdits(
        desc: CompoundActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): AllowedDataEdits | undefined {
        const compound = getBuilderAction(desc, ccc)?.action;
        if (compound === undefined) return undefined;

        const dataEdits = makeEmptyMutableAllowedDataEdits();

        function processFlow(flow: FlowActionNode): void {
            for (const { actionDescription } of flow.actions) {
                const handler = handlerForActionKind(actionDescription.kind);
                if (handler === undefined) return undefined;

                const edits = handler.getAllowedDataEdits(actionDescription, tables, ccc, mutatingScreenKind);
                mergeAllowedDataEdits(dataEdits, edits);
            }
        }

        processCompoundActionFlow(compound, processFlow);

        return dataEdits;
    }

    public async duplicateAction(
        desc: CompoundActionDescription,
        copyTables: InputOutputTables | undefined,
        iccc: InteractiveComponentConfiguratorContext,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        _screensCreated: Set<string>
    ): Promise<CompoundActionDescription | undefined> {
        if (copyTables === undefined) return undefined;

        const builderAction = getBuilderAction(desc, iccc);
        if (builderAction === undefined) return undefined;

        const perApp = builderAction.perApp[iccc.appID];
        if (perApp === undefined) return undefined;

        if (!areTableNamesEqual(perApp.tableName, getTableName(copyTables.input))) return undefined;

        const actionID = await iccc.saveBuilderAction(
            undefined,
            perApp.tableName,
            `Copy of ${builderAction.title ?? "action"}`,
            builderAction.description,
            builderAction.action
        );
        return { ...desc, actionID: makeCompoundActionProperty(actionID) };
    }

    public rewriteAfterReload(
        desc: CompoundActionDescription,
        tables: InputOutputTables,
        ccc: RewritingComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): CompoundActionDescription | undefined {
        const builderAction = getBuilderAction(desc, ccc);
        // We're not discarding it, because the action might simply have failed to
        // load, in which case the `ScreenDescriptionBuilder` will retain all screens,
        // since any screen might be referenced by this action.
        if (builderAction === undefined) return desc;

        function processFlow(flow: FlowActionNode): void {
            for (const { actionDescription } of flow.actions) {
                const handler = handlerForActionKind(actionDescription.kind);
                if (handler === undefined) continue;
                // We're not actually rewriting the builder actions, but we need
                // to run the rewriting code so that linked screens will be
                // included.
                handler.rewriteAfterReload(actionDescription, tables, ccc, mutatingScreenKind);
            }
        }

        processCompoundActionFlow(builderAction.action, processFlow);

        return desc;
    }

    public inflate(ib: WireInflationBackend, desc: CompoundActionDescription): WireActionHydrator | undefined {
        const builderAction = getBuilderAction(desc, ib);
        if (builderAction === undefined) return undefined;
        const root = builderAction.action;

        const conditionPredicates = new Map<Formula, WirePredicate>();
        const actionHydrators = new Map<ActionDescription, WireActionHydrator>();

        function inflateFlow(flow: FlowActionNode): void {
            for (const { actionDescription } of flow.actions) {
                const hydrator = inflateActions(ib, [actionDescription]);
                if (hydrator === undefined) continue;

                actionHydrators.set(actionDescription, hydrator);
            }
        }

        for (const conditionalFlow of root.conditionalFlows) {
            const predicate = ib.inflatePredicate(conditionalFlow.condition);
            if (predicate === undefined) continue;

            conditionPredicates.set(conditionalFlow.condition, predicate);
            inflateFlow(conditionalFlow.flow);
        }
        if (root.elseFlow !== undefined) {
            inflateFlow(root.elseFlow);
        }

        return vp => {
            const actionRunners: WireActionRunner[] = [];
            let didHydrate = false;
            let haveMissing = false;

            function hydrateFlow(flow: FlowActionNode): void {
                didHydrate = true;
                for (const { actionDescription } of flow.actions) {
                    const runner = actionHydrators.get(actionDescription)?.(vp);
                    if (runner === undefined) {
                        haveMissing = true;
                        return;
                    }
                    actionRunners.push(runner);
                }
                return;
            }

            for (const conditionalFlow of root.conditionalFlows) {
                const predicate = conditionPredicates.get(conditionalFlow.condition);
                if (predicate === undefined) continue;

                if (predicate(vp)) {
                    hydrateFlow(conditionalFlow.flow);
                    break;
                }
            }

            if (!didHydrate && root.elseFlow !== undefined) {
                hydrateFlow(root.elseFlow);
            }

            if (actionRunners.length === 0) return undefined;

            return async ab => {
                for (const runner of actionRunners) {
                    const success = await runner(ab);
                    if (!success) return false;
                }
                return !haveMissing;
            };
        };
    }
}
